import { Pencil } from "lucide-react";
import { Button, Dialog, Tabs, Tag, Descriptions } from "~/components/ui";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { LexiconConceptResponse } from "@noa/api-core";
import type { AppLanguageType } from "@noa/i18n";
import { lexiconModalClassNames } from "~/utils/lexicon";

type ViewConceptModalProps = {
  concept: LexiconConceptResponse | null;
  language: AppLanguageType;
  close: () => void;
  onEdit: (concept: LexiconConceptResponse) => void;
};

export function ViewConceptModal({ concept, language, close, onEdit }: ViewConceptModalProps) {
  const { t } = useTranslation("lexicon");
  const tabsKey = `${concept?.unique ?? ""}-${language}`;

  const renderDetails = (lang: AppLanguageType) => {
    if (!concept) return null;
    const subject = lang === "fr" ? concept.subject_fr : concept.subject_en;
    const synonyms = lang === "fr" ? concept.synonyms_fr : concept.synonyms_en;
    const acronyms = lang === "fr" ? concept.acronyms_fr : concept.acronyms_en;

    return (
      <Descriptions
        column={1}
        bordered
        size="small"
        layout="responsive"
        items={[
          { key: "term", label: t("fields.term"), children: lang === "fr" ? concept.term_fr : concept.term_en },
          {
            key: "description",
            label: t("fields.description"),
            children: lang === "fr" ? concept.description_fr : concept.description_en,
          },
          !!subject && { key: "subject", label: t("fields.subject"), children: subject },
          { key: "synonyms", label: t("fields.synonyms"), children: synonyms.map((s) => <Tag key={s}>{s}</Tag>) },
          { key: "acronyms", label: t("fields.acronyms"), children: acronyms.map((a) => <Tag key={a}>{a}</Tag>) },
        ]}
      />
    );
  };

  const handleEdit = useCallback(() => {
    if (concept) {
      close();
      onEdit(concept);
    }
  }, [concept, close, onEdit]);

  return (
    <Dialog
      title={
        concept ? (
          <div className="flex min-w-0 items-center gap-2 pr-8">
            <span className="flex-1 min-w-0 truncate">
              {language === "fr" ? concept.term_fr.toLocaleUpperCase() : concept.term_en.toLocaleUpperCase()}
            </span>
            <Button
              variant="text"
              size="small"
              icon={<Pencil size={14} />}
              onClick={handleEdit}
              title={t("actions.update")}
              className="shrink-0"
            />
          </div>
        ) : (
          ""
        )
      }
      open={!!concept}
      onCancel={close}
      footer={null}
      classNames={lexiconModalClassNames}
    >
      {concept && (
        <Tabs
          key={tabsKey}
          defaultActiveKey={language}
          items={[
            {
              key: "fr",
              label: t("tabs.french"),
              children: renderDetails("fr"),
            },
            {
              key: "en",
              label: t("tabs.english"),
              children: renderDetails("en"),
            },
          ]}
        />
      )}
    </Dialog>
  );
}
